import { Suspense, useEffect, useRef, useState } from 'react'
import Debug from 'debug'
import { observer } from 'mobx-react-lite'
import { useQuery } from '@connectrpc/connect-query'
import { ErrorBoundary } from '@sentry/react'

import { getVideoPlaylistLink } from '@jotta/grpc-connect-openapi/photoQuery'
import { Box } from '@jotta/ui/Box'
import { lazyWithRetry } from '@jotta/ui/lazyWithRetry'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'

import type { CarouselPhoto } from '../../api/usePhoto'

import { usePhotoLargeThumb } from './usePhotoLargeThumb'

const debug = Debug('jotta:photos:MediaView')
const VideoPlayer = lazyWithRetry(
  () => import(/* webpackChunkName: "VideoPlayer" */ '@jotta/video'),
)

const nativeHlsSupported = (() => {
  if (typeof document === 'undefined') {
    return false
  }

  const video = document.createElement('video')
  return Boolean(
    video.canPlayType('application/vnd.apple.mpegurl') ||
    video.canPlayType('application/x-mpegURL'),
  )
})()

function playVideo(video: HTMLVideoElement) {
  void video.play().catch(() => undefined)
}

export interface MediaViewProps {
  item: CarouselPhoto
  active?: boolean
  slideShowMode?: boolean
  setPlaying?: (active: boolean) => void
}

export const MediaView = observer<MediaViewProps>(function MediaView({
  item,
  active = false,
  slideShowMode,
  setPlaying,
}) {
  const { expiringSmallThumbUrl, ref, fileName, isLivePhoto } = item
  const livePhotoRef = useRef<HTMLVideoElement>(null)
  const [livePhotoEnded, setLivePhotoEnded] = useState(false)
  const [livePhotoPlaylistFailed, setLivePhotoPlaylistFailed] = useState(false)
  const { url: largeThumbUrl } = usePhotoLargeThumb(ref)
  // Prefer the LARGE thumbnail once it resolves; show the small preview
  // in the meantime so the slide isn't blank during the gRPC round-trip.
  const thumbUrl = largeThumbUrl ?? expiringSmallThumbUrl

  const { data: videoPlayListURLs } = useQuery(
    getVideoPlaylistLink,
    {
      ref,
    },
    {
      refetchOnMount: false,
      staleTime: 300_000,
      enabled: Boolean(item.videoDurationSeconds || item.isLivePhoto),
      select: data => {
        const result = data.result
        switch (result?.case) {
          case 'playlistLink':
            return result.value
          case undefined:
          case 'videoNotFound':
            return undefined
          default:
            return exhaustiveGuard(result)
        }
      },
    },
  )
  const videoUrlOriginal = videoPlayListURLs?.urlOriginal
  const videoUrlPlaylist = videoPlayListURLs?.urlPlaylist
  const livePhotoVideoUrl =
    nativeHlsSupported && !livePhotoPlaylistFailed
      ? (videoUrlPlaylist ?? videoUrlOriginal)
      : videoUrlOriginal

  useEffect(() => {
    setLivePhotoEnded(false)
    setLivePhotoPlaylistFailed(false)
  }, [active, videoUrlOriginal, videoUrlPlaylist])

  useEffect(() => {
    if (!isLivePhoto || !active || livePhotoEnded || !livePhotoVideoUrl) {
      return
    }

    const video = livePhotoRef.current
    if (!video) {
      return
    }

    // Safari is strict about autoplay and source selection for live photos.
    // Set inline/muted playback and the chosen src before loading and playing.
    video.muted = true
    video.playsInline = true
    video.setAttribute('playsinline', '')
    video.setAttribute('webkit-playsinline', '')
    video.src = livePhotoVideoUrl
    video.load()
  }, [active, isLivePhoto, livePhotoEnded, livePhotoVideoUrl])

  if (!isLivePhoto && videoUrlOriginal) {
    return (
      <div className="flex size-full flex-auto py-16">
        <ErrorBoundary
          beforeCapture={scope => {
            scope.setTag('reactComponent', 'VideoPlayer')
            scope.setTag('jottaModule', 'MediaCarousel')
          }}
          onError={(error, componentStack, eventId) => {
            debug(error, componentStack, eventId)
          }}
        >
          <Suspense
            fallback={
              <LoadingOverlay
                id="MediaView"
                open
                position="absolute"
                darkMode
              />
            }
          >
            <VideoPlayer
              active={active}
              playListURL={videoUrlPlaylist}
              videoURL={videoUrlOriginal}
              poster={thumbUrl}
              autoPlay={slideShowMode}
              name={fileName}
              rewindOnEnd
              playerProps={{
                onPlay: () => setPlaying && setPlaying(true),
                onPause: () => setPlaying && setPlaying(false),
                onEnded: () => setPlaying && setPlaying(false),
              }}
            />
          </Suspense>
        </ErrorBoundary>
      </div>
    )
  } else if (isLivePhoto && active && !livePhotoEnded && livePhotoVideoUrl) {
    return (
      <video
        ref={livePhotoRef}
        className="max-h-full"
        key={livePhotoVideoUrl}
        poster={thumbUrl}
        autoPlay
        draggable={false}
        disablePictureInPicture
        playsInline
        muted
        onLoadedMetadata={event => {
          playVideo(event.currentTarget)
        }}
        onEnded={() => setLivePhotoEnded(true)}
        onError={() => {
          if (livePhotoVideoUrl === videoUrlPlaylist && videoUrlOriginal) {
            setLivePhotoPlaylistFailed(true)
            return
          }
          setLivePhotoEnded(true)
        }}
      />
    )
  } else {
    return (
      <div className="relative size-full bg-no-repeat object-contain object-center">
        <Box variant="layout.mediaItemBackground" />
        <img
          alt=""
          src={thumbUrl}
          className="size-full"
          draggable={false}
          onDragStart={e => e.preventDefault()}
        />
      </div>
    )
  }
})
